import React, { useEffect, useRef } from 'react';
import { Pagination } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { MatchRow } from './types';
import ResultsToolbar, { FilterChip } from './ResultsToolbar';

export type SortKey = 'name' | 'hs' | 'list' | 'agency' | 'country' | 'date';

export type { FilterChip };

interface Props {
  rows: MatchRow[];
  shownCount: number;
  total: number;
  filteredByText: boolean;
  query: string;
  onQuery: (v: string) => void;
  sortKey: SortKey | '';
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  onOpenRow: (r: MatchRow) => void;
  activeRow: MatchRow | null;
  chips: FilterChip[];
  onOpenFilters: () => void;
  onClearFilters: () => void;
  onDownloadCsv: () => void;
  onResetTable: () => void;
  resetDisabled: boolean;
  page: number;
  pageCount: number;
  onPage: (n: number) => void;
  rangeText: string;
}

export const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Product / Match' },
  { key: 'hs', label: 'HTS / HS Code' },
  { key: 'list', label: 'List Name' },
  { key: 'agency', label: 'Agency' },
  { key: 'country', label: 'Country' },
  { key: 'date', label: 'Detection Date' },
];

const SortArrow: React.FC<{ dir: 'asc' | 'desc'; active: boolean }> = ({
  dir,
  active,
}) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    aria-hidden='true'
    className={active ? 'active' : ''}
  >
    <path
      d={
        dir === 'asc'
          ? 'M8 12.6666V3.33331M8 3.33331L3.33333 7.99998M8 3.33331L12.6667 7.99998'
          : 'M8 3.33331V12.6666M8 12.6666L3.33333 7.99998M8 12.6666L12.6667 7.99998'
      }
      stroke='currentColor'
      strokeWidth='1.33'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ResultsTable: React.FC<Props> = (props) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [props.page, props.sortKey, props.sortDir, props.query]);

  return (
    <>
      <ResultsToolbar
        searchId='psc-f'
        searchLabel='Search these matches'
        placeholder='Search by product, HTS, list…'
        query={props.query}
        onQuery={props.onQuery}
        count={
          <span className='psc-count'>
            <strong>{props.shownCount.toLocaleString()}</strong>{' '}
            {props.filteredByText
              ? `of ${props.total.toLocaleString()} matches`
              : props.shownCount === 1
              ? 'match'
              : 'matches'}
          </span>
        }
        filterCount={props.chips.length}
        onOpenFilters={props.onOpenFilters}
        onReset={props.onResetTable}
        resetLabel='Reset search and sort'
        resetDisabled={props.resetDisabled}
        chips={props.chips}
        onClearFilters={props.onClearFilters}
        actions={
          <button
            type='button'
            className='psc-btn'
            onClick={props.onDownloadCsv}
            aria-label='Download results CSV'
            title='Download results CSV'
          >
            <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
            Download CSV
          </button>
        }
      />

      <div className='entity-results-table-card psc-table-card psc-settle'>
        <div ref={scrollRef} className='entity-results-table-scroll'>
          <table className='entity-results-table psc-table'>
            <thead>
              <tr>
                {COLUMNS.map((c) => {
                  const on = props.sortKey === c.key;
                  const asc = props.sortDir === 'asc';
                  return (
                    <th
                      key={c.key}
                      aria-sort={
                        on ? (asc ? 'ascending' : 'descending') : 'none'
                      }
                    >
                      <button
                        type='button'
                        className='entity-results-sort-header'
                        onClick={() => props.onSort(c.key)}
                      >
                        {c.label}
                        <SortArrow
                          dir={on && !asc ? 'desc' : 'asc'}
                          active={on}
                        />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {props.rows.map((r, i) => (
                <tr
                  key={`${r.id}-${i}`}
                  className={`psc-row${
                    props.activeRow === r ? ' selected-row' : ''
                  }`}
                  onClick={() => props.onOpenRow(r)}
                >
                  <td className='entity-results-name'>
                    <button
                      type='button'
                      className='psc-row-open'
                      title={r.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onOpenRow(r);
                      }}
                    >
                      {r.name}
                    </button>
                  </td>
                  <td className='tnum'>{r.hs}</td>
                  <td title={r.list}>{r.list}</td>
                  <td title={r.agency}>{r.agency}</td>
                  <td title={r.country}>{r.country}</td>
                  <td className='tnum'>{r.date}</td>
                </tr>
              ))}
              {props.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    className='entity-results-empty psc-table-empty'
                  >
                    {props.total > 0 ? (
                      <>
                        <h3>No rows match this search</h3>
                        <p>
                          The screen found {props.total.toLocaleString()}{' '}
                          {props.total === 1 ? 'match' : 'matches'}; none of
                          them contain “{props.query}”. The boxes above still
                          describe all of them.
                        </p>
                        <button
                          type='button'
                          className='psc-btn psc-table-empty-btn'
                          onClick={() => props.onQuery('')}
                        >
                          Clear search
                        </button>
                      </>
                    ) : (
                      <>
                        <h3>No matches</h3>
                        <p>
                          Nothing on the lists in scope matches this screen. An
                          empty screen is still a result, and you can save it.
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='entity-results-footer'>
          <span className='tnum'>{props.rangeText}</span>
          {props.pageCount > 1 && (
            <div className='entity-results-pagination hb-pagination'>
              <Pagination
                count={props.pageCount}
                page={props.page + 1}
                onChange={(_, n) => props.onPage(n - 1)}
                color='primary'
                siblingCount={1}
                boundaryCount={1}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ResultsTable;
